import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  BookOpenIcon,
  ArrowLeftIcon,
  ClipboardDocumentIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const BASE_URL = 'https://ledgora.be/api';

const SECTIONS = [
  { id: 'overview', tKey: 'apiDocs.sections.overview' },
  { id: 'auth', tKey: 'apiDocs.sections.auth' },
  { id: 'quickstart', tKey: 'apiDocs.sections.quickstart' },
  { id: 'scopes', tKey: 'apiDocs.sections.scopes' },
  { id: 'pagination', tKey: 'apiDocs.sections.pagination' },
  { id: 'errors', tKey: 'apiDocs.sections.errors' },
  { id: 'endpoints', tKey: 'apiDocs.sections.endpoints' },
  { id: 'rate', tKey: 'apiDocs.sections.rate' },
] as const;

const SCOPES = [
  'invoices:read',
  'invoices:write',
  'clients:read',
  'clients:write',
  'quotes:read',
  'quotes:write',
  'expenses:read',
  'expenses:write',
  'documents:read',
  'payments:read',
];

const ENDPOINTS: { group: string; rows: [string, string, string][] }[] = [
  {
    group: 'Clients',
    rows: [
      ['GET', '/clients', 'clients:read'],
      ['GET', '/clients/{id}', 'clients:read'],
      ['POST', '/clients', 'clients:write'],
      ['PUT', '/clients/{id}', 'clients:write'],
      ['DELETE', '/clients/{id}', 'clients:write'],
    ],
  },
  {
    group: 'Invoices',
    rows: [
      ['GET', '/invoices', 'invoices:read'],
      ['GET', '/invoices/{id}', 'invoices:read'],
      ['POST', '/invoices', 'invoices:write'],
      ['PUT', '/invoices/{id}', 'invoices:write'],
      ['DELETE', '/invoices/{id}', 'invoices:write'],
      ['POST', '/invoices/{id}/send-email', 'invoices:write'],
      ['POST', '/invoices/{id}/duplicate', 'invoices:write'],
      ['GET', '/invoices/{id}/pdf', 'invoices:read'],
      ['POST', '/invoices/{id}/payments', 'invoices:write'],
    ],
  },
  {
    group: 'Quotes',
    rows: [
      ['GET', '/quotes', 'quotes:read'],
      ['GET', '/quotes/{id}', 'quotes:read'],
      ['POST', '/quotes', 'quotes:write'],
      ['PUT', '/quotes/{id}', 'quotes:write'],
      ['POST', '/quotes/{id}/convert', 'quotes:write + invoices:write'],
    ],
  },
  {
    group: 'Expenses',
    rows: [
      ['GET', '/expenses', 'expenses:read'],
      ['GET', '/expenses/{id}', 'expenses:read'],
      ['POST', '/expenses', 'expenses:write'],
      ['PUT', '/expenses/{id}', 'expenses:write'],
      ['DELETE', '/expenses/{id}', 'expenses:write'],
    ],
  },
  {
    group: 'Documents',
    rows: [
      ['GET', '/documents', 'documents:read'],
      ['GET', '/documents/{id}/download', 'documents:read'],
    ],
  },
  {
    group: 'Payments',
    rows: [
      ['GET', '/invoices/{id}/payments', 'payments:read'],
    ],
  },
];

const ERRORS: [string, string][] = [
  ['400', 'apiDocs.errors.400'],
  ['401', 'apiDocs.errors.401'],
  ['403', 'apiDocs.errors.403'],
  ['404', 'apiDocs.errors.404'],
  ['422', 'apiDocs.errors.422'],
  ['429', 'apiDocs.errors.429'],
  ['5xx', 'apiDocs.errors.5xx'],
];

export default function ApiDocsPage() {
  const { t } = useTranslation();

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <Link
            to="/settings/api-keys"
            className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-2"
          >
            <ArrowLeftIcon className="w-4 h-4" /> {t('apiDocs.backToKeys')}
          </Link>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BookOpenIcon className="w-6 h-6" /> {t('apiDocs.title')}
          </h1>
          <p className="text-sm text-gray-500 mt-1">{t('apiDocs.subtitle')}</p>
        </div>
        <Link to="/settings/api-keys" className="btn btn-primary">
          {t('apiDocs.manageKeys')}
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[200px_1fr] gap-6">
        {/* TOC */}
        <aside className="lg:sticky lg:top-4 h-fit">
          <nav className="card card-body p-3 flex lg:flex-col gap-1 overflow-x-auto">
            {SECTIONS.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className="px-3 py-1.5 text-sm rounded-md text-gray-600 hover:bg-gray-100 hover:text-gray-900 whitespace-nowrap"
              >
                {t(s.tKey)}
              </a>
            ))}
          </nav>
        </aside>

        {/* Content */}
        <div className="space-y-6 min-w-0">
          {/* Overview */}
          <Section id="overview" title={t('apiDocs.sections.overview')}>
            <p>{t('apiDocs.overview.intro')}</p>
            <div className="mt-4 overflow-x-auto rounded-lg border border-gray-200">
              <table className="min-w-full text-sm">
                <tbody className="divide-y divide-gray-100">
                  <InfoRow label={t('apiDocs.overview.baseUrl')} value={<code className="font-mono text-xs">{BASE_URL}</code>} />
                  <InfoRow label={t('apiDocs.overview.authScheme')} value={<code className="font-mono text-xs">Authorization: Bearer awk_live_…</code>} />
                  <InfoRow label={t('apiDocs.overview.contentType')} value={<code className="font-mono text-xs">application/json</code>} />
                  <InfoRow label={t('apiDocs.overview.rateLimit')} value={t('apiDocs.overview.rateLimitValue')} />
                  <InfoRow label={t('apiDocs.overview.timeFormat')} value={<code className="font-mono text-xs">ISO 8601</code>} />
                </tbody>
              </table>
            </div>
            <Note>{t('apiDocs.overview.scopedNote')}</Note>
          </Section>

          {/* Auth */}
          <Section id="auth" title={t('apiDocs.sections.auth')}>
            <p>{t('apiDocs.auth.intro')}</p>
            <CodeBlock
              language="http"
              code={`GET /api/invoices HTTP/1.1
Host: ledgora.be
Authorization: Bearer awk_live_5f3b8c…
Accept: application/json`}
            />
            <h3 className="font-semibold text-gray-900 mt-6 mb-2">{t('apiDocs.auth.createTitle')}</h3>
            <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700">
              <li>{t('apiDocs.auth.step1')}</li>
              <li>{t('apiDocs.auth.step2')}</li>
              <li>{t('apiDocs.auth.step3')}</li>
              <li>{t('apiDocs.auth.step4')}</li>
            </ol>
            <h3 className="font-semibold text-gray-900 mt-6 mb-2">{t('apiDocs.auth.bestPracticesTitle')}</h3>
            <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
              <li>{t('apiDocs.auth.bp1')}</li>
              <li>{t('apiDocs.auth.bp2')}</li>
              <li>{t('apiDocs.auth.bp3')}</li>
              <li>{t('apiDocs.auth.bp4')}</li>
            </ul>
          </Section>

          {/* Quickstart */}
          <Section id="quickstart" title={t('apiDocs.sections.quickstart')}>
            <p>{t('apiDocs.quickstart.intro')}</p>
            <CodeTabs
              tabs={[
                {
                  label: 'cURL',
                  language: 'bash',
                  code: `curl ${BASE_URL}/invoices?limit=10 \\
  -H "Authorization: Bearer $AWK_TOKEN" \\
  -H "Accept: application/json"`,
                },
                {
                  label: 'JavaScript',
                  language: 'javascript',
                  code: `const res = await fetch('${BASE_URL}/invoices?limit=10', {
  headers: {
    Authorization: \`Bearer \${process.env.AWK_TOKEN}\`,
    Accept: 'application/json',
  },
});
const { data, meta } = await res.json();
console.log(\`\${meta.total} invoices\`, data);`,
                },
                {
                  label: 'Python',
                  language: 'python',
                  code: `import os, requests

res = requests.get(
    '${BASE_URL}/invoices',
    params={'limit': 10},
    headers={'Authorization': f"Bearer {os.environ['AWK_TOKEN']}"},
)
res.raise_for_status()
payload = res.json()
print(payload['meta']['total'], 'invoices')`,
                },
                {
                  label: 'PHP',
                  language: 'php',
                  code: `<?php
$ch = curl_init('${BASE_URL}/invoices?limit=10');
curl_setopt_array($ch, [
    CURLOPT_RETURNTRANSFER => true,
    CURLOPT_HTTPHEADER => [
        'Authorization: Bearer ' . getenv('AWK_TOKEN'),
        'Accept: application/json',
    ],
]);
$payload = json_decode(curl_exec($ch), true);
curl_close($ch);
echo $payload['meta']['total'] . " invoices\\n";`,
                },
                {
                  label: 'Node.js',
                  language: 'javascript',
                  code: `import axios from 'axios';

const api = axios.create({
  baseURL: '${BASE_URL}',
  headers: { Authorization: \`Bearer \${process.env.AWK_TOKEN}\` },
});

const { data } = await api.get('/invoices', { params: { limit: 10 } });
console.log(data.meta.total, 'invoices');`,
                },
              ]}
            />

            <h3 className="font-semibold text-gray-900 mt-6 mb-2">{t('apiDocs.quickstart.createTitle')}</h3>
            <p>{t('apiDocs.quickstart.createIntro')}</p>
            <CodeTabs
              tabs={[
                {
                  label: 'cURL',
                  language: 'bash',
                  code: `curl -X POST ${BASE_URL}/invoices \\
  -H "Authorization: Bearer $AWK_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "clientId": "01939e6c-…",
    "issueDate": "2026-06-01",
    "dueDate": "2026-06-30",
    "currency": "EUR",
    "lines": [
      { "description": "Consulting", "quantity": 10, "unitPrice": "120.00", "vatRate": "21.00" }
    ]
  }'`,
                },
                {
                  label: 'JavaScript',
                  language: 'javascript',
                  code: `const res = await fetch('${BASE_URL}/invoices', {
  method: 'POST',
  headers: {
    Authorization: \`Bearer \${process.env.AWK_TOKEN}\`,
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({
    clientId: '01939e6c-…',
    issueDate: '2026-06-01',
    dueDate: '2026-06-30',
    currency: 'EUR',
    lines: [
      { description: 'Consulting', quantity: 10, unitPrice: '120.00', vatRate: '21.00' },
    ],
  }),
});
const { data: invoice } = await res.json();`,
                },
                {
                  label: 'Python',
                  language: 'python',
                  code: `import os, requests

invoice = requests.post(
    '${BASE_URL}/invoices',
    headers={'Authorization': f"Bearer {os.environ['AWK_TOKEN']}"},
    json={
        'clientId': '01939e6c-…',
        'issueDate': '2026-06-01',
        'dueDate': '2026-06-30',
        'currency': 'EUR',
        'lines': [
            {'description': 'Consulting', 'quantity': 10,
             'unitPrice': '120.00', 'vatRate': '21.00'},
        ],
    },
).json()['data']`,
                },
                {
                  label: 'PHP',
                  language: 'php',
                  code: `<?php
$payload = [
    'clientId'  => '01939e6c-…',
    'issueDate' => '2026-06-01',
    'dueDate'   => '2026-06-30',
    'currency'  => 'EUR',
    'lines'     => [[
        'description' => 'Consulting',
        'quantity'    => 10,
        'unitPrice'   => '120.00',
        'vatRate'     => '21.00',
    ]],
];

$ch = curl_init('${BASE_URL}/invoices');
curl_setopt_array($ch, [
    CURLOPT_RETURNTRANSFER => true,
    CURLOPT_POST           => true,
    CURLOPT_POSTFIELDS     => json_encode($payload),
    CURLOPT_HTTPHEADER     => [
        'Authorization: Bearer ' . getenv('AWK_TOKEN'),
        'Content-Type: application/json',
    ],
]);
$invoice = json_decode(curl_exec($ch), true)['data'];
curl_close($ch);`,
                },
              ]}
            />

            <h3 className="font-semibold text-gray-900 mt-6 mb-2">{t('apiDocs.quickstart.errorTitle')}</h3>
            <p>{t('apiDocs.quickstart.errorIntro')}</p>
            <CodeTabs
              tabs={[
                {
                  label: 'JavaScript',
                  language: 'javascript',
                  code: `const res = await fetch(\`\${API}/invoices\`, { headers });
if (!res.ok) {
  const { error, errorCode } = await res.json();
  throw new Error(\`[\${res.status}] \${errorCode ?? 'error'}: \${error}\`);
}`,
                },
                {
                  label: 'Python',
                  language: 'python',
                  code: `res = requests.get(f'{API}/invoices', headers=headers)
if not res.ok:
    body = res.json()
    raise RuntimeError(f"[{res.status_code}] {body.get('errorCode')}: {body['error']}")`,
                },
                {
                  label: 'PHP',
                  language: 'php',
                  code: `<?php
$status = curl_getinfo($ch, CURLINFO_HTTP_CODE);
$body   = json_decode(curl_exec($ch), true);
if ($status >= 400) {
    throw new RuntimeException(
        "[$status] " . ($body['errorCode'] ?? '') . ': ' . $body['error']
    );
}`,
                },
              ]}
            />
          </Section>

          {/* Scopes */}
          <Section id="scopes" title={t('apiDocs.sections.scopes')}>
            <p>{t('apiDocs.scopes.intro')}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {SCOPES.map((s) => (
                <code
                  key={s}
                  className="font-mono text-xs px-2 py-1 rounded-md bg-primary-50 text-primary-700 border border-primary-100"
                >
                  {s}
                </code>
              ))}
            </div>
            <Note>{t('apiDocs.scopes.forbidden')}</Note>
          </Section>

          {/* Pagination */}
          <Section id="pagination" title={t('apiDocs.sections.pagination')}>
            <p>{t('apiDocs.pagination.intro')}</p>
            <CodeBlock
              language="json"
              code={`{
  "data": [ /* items */ ],
  "meta": {
    "total": 124,
    "page": 1,
    "limit": 20,
    "pages": 7
  }
}`}
            />
          </Section>

          {/* Errors */}
          <Section id="errors" title={t('apiDocs.sections.errors')}>
            <p>{t('apiDocs.errors.intro')}</p>
            <CodeBlock
              language="json"
              code={`{
  "error": "Invalid or revoked API key.",
  "errorCode": "auth.invalid_key"
}`}
            />
            <div className="mt-4 overflow-x-auto rounded-lg border border-gray-200">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('apiDocs.errors.status')}</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('apiDocs.errors.meaning')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {ERRORS.map(([code, key]) => (
                    <tr key={code}>
                      <td className="px-4 py-2"><code className="font-mono text-xs">{code}</code></td>
                      <td className="px-4 py-2 text-gray-700">{t(key)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          {/* Endpoints */}
          <Section id="endpoints" title={t('apiDocs.sections.endpoints')}>
            <p>{t('apiDocs.endpoints.intro')}</p>
            <div className="space-y-6 mt-4">
              {ENDPOINTS.map((group) => (
                <div key={group.group}>
                  <h3 className="font-semibold text-gray-900 mb-2">{group.group}</h3>
                  <div className="overflow-x-auto rounded-lg border border-gray-200">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase w-24">{t('apiDocs.endpoints.method')}</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('apiDocs.endpoints.path')}</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('apiDocs.endpoints.scope')}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {group.rows.map(([method, path, scope]) => (
                          <tr key={method + path}>
                            <td className="px-4 py-2"><MethodBadge method={method} /></td>
                            <td className="px-4 py-2"><code className="font-mono text-xs text-gray-800">{path}</code></td>
                            <td className="px-4 py-2"><code className="font-mono text-xs text-primary-700">{scope}</code></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>

            <h3 className="font-semibold text-gray-900 mt-6 mb-2">{t('apiDocs.endpoints.exampleTitle')}</h3>
            <CodeTabs
              tabs={[
                {
                  label: 'cURL',
                  language: 'bash',
                  code: `curl -X POST ${BASE_URL}/clients \\
  -H "Authorization: Bearer $AWK_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "ACME SA",
    "type": "company",
    "email": "billing@acme.example",
    "vatNumber": "BE0123456789",
    "countryCode": "BE",
    "city": "Brussels",
    "postalCode": "1000",
    "address": "Rue de la Loi 1"
  }'`,
                },
                {
                  label: 'JavaScript',
                  language: 'javascript',
                  code: `await fetch('${BASE_URL}/clients', {
  method: 'POST',
  headers: {
    Authorization: \`Bearer \${process.env.AWK_TOKEN}\`,
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({
    name: 'ACME SA',
    type: 'company',
    email: 'billing@acme.example',
    vatNumber: 'BE0123456789',
    countryCode: 'BE',
    city: 'Brussels',
    postalCode: '1000',
    address: 'Rue de la Loi 1',
  }),
});`,
                },
                {
                  label: 'Python',
                  language: 'python',
                  code: `requests.post(
    '${BASE_URL}/clients',
    headers={'Authorization': f"Bearer {os.environ['AWK_TOKEN']}"},
    json={
        'name': 'ACME SA',
        'type': 'company',
        'email': 'billing@acme.example',
        'vatNumber': 'BE0123456789',
        'countryCode': 'BE',
        'city': 'Brussels',
        'postalCode': '1000',
        'address': 'Rue de la Loi 1',
    },
)`,
                },
              ]}
            />
          </Section>

          {/* Rate */}
          <Section id="rate" title={t('apiDocs.sections.rate')}>
            <p>{t('apiDocs.rate.intro')}</p>
            <div className="mt-4 overflow-x-auto rounded-lg border border-gray-200">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('apiDocs.rate.header')}</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('apiDocs.rate.description')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  <tr><td className="px-4 py-2"><code className="font-mono text-xs">X-RateLimit-Limit</code></td><td className="px-4 py-2 text-gray-700">{t('apiDocs.rate.h1')}</td></tr>
                  <tr><td className="px-4 py-2"><code className="font-mono text-xs">X-RateLimit-Remaining</code></td><td className="px-4 py-2 text-gray-700">{t('apiDocs.rate.h2')}</td></tr>
                  <tr><td className="px-4 py-2"><code className="font-mono text-xs">X-RateLimit-Reset</code></td><td className="px-4 py-2 text-gray-700">{t('apiDocs.rate.h3')}</td></tr>
                  <tr><td className="px-4 py-2"><code className="font-mono text-xs">Retry-After</code></td><td className="px-4 py-2 text-gray-700">{t('apiDocs.rate.h4')}</td></tr>
                </tbody>
              </table>
            </div>
            <Note>
              {t('apiDocs.rate.contact')}{' '}
              <a href="mailto:support@ledgora.be" className="text-primary-600 hover:underline">support@ledgora.be</a>.
            </Note>
          </Section>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="card card-body scroll-mt-4">
      <h2 className="text-xl font-bold text-gray-900 mb-3">{title}</h2>
      <div className="text-sm text-gray-700 leading-relaxed">{children}</div>
    </section>
  );
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <tr>
      <td className="px-4 py-2 text-gray-500 w-1/3">{label}</td>
      <td className="px-4 py-2 text-gray-800">{value}</td>
    </tr>
  );
}

function Note({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-4 rounded-lg bg-primary-50 border border-primary-100 px-4 py-3 text-sm text-primary-900">
      {children}
    </div>
  );
}

function MethodBadge({ method }: { method: string }) {
  const colors: Record<string, string> = {
    GET: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    POST: 'bg-blue-50 text-blue-700 border-blue-200',
    PUT: 'bg-amber-50 text-amber-700 border-amber-200',
    PATCH: 'bg-amber-50 text-amber-700 border-amber-200',
    DELETE: 'bg-red-50 text-red-700 border-red-200',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-mono font-semibold border ${colors[method] ?? 'bg-gray-50 text-gray-700 border-gray-200'}`}>
      {method}
    </span>
  );
}

function CodeBlock({ code, language }: { code: string; language: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  async function copy() {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    toast.success(t('apiKeys.copied'));
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <div className="mt-3 relative group">
      <div className="absolute top-2 right-2 flex items-center gap-2 z-10">
        <span className="text-[10px] uppercase tracking-wider text-gray-400 font-mono">{language}</span>
        <button
          type="button"
          onClick={copy}
          className="p-1.5 rounded-md bg-gray-800/60 text-gray-300 hover:bg-gray-700 hover:text-white transition-colors"
          title={t('common.copy')}
        >
          {copied ? <CheckIcon className="w-4 h-4" /> : <ClipboardDocumentIcon className="w-4 h-4" />}
        </button>
      </div>
      <pre className="rounded-lg bg-gray-900 text-gray-100 text-xs leading-relaxed p-4 pr-20 overflow-x-auto">
        <code>{code}</code>
      </pre>
    </div>
  );
}

type CodeTab = { label: string; language: string; code: string };

function CodeTabs({ tabs }: { tabs: CodeTab[] }) {
  const { t } = useTranslation();
  const [active, setActive] = useState(0);
  const [copied, setCopied] = useState(false);
  const current = tabs[active];

  async function copy() {
    await navigator.clipboard.writeText(current.code);
    setCopied(true);
    toast.success(t('apiKeys.copied'));
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <div className="mt-3 rounded-lg overflow-hidden border border-gray-800">
      <div className="flex items-center justify-between bg-gray-800 pl-1 pr-2">
        <div className="flex overflow-x-auto">
          {tabs.map((tab, i) => (
            <button
              key={tab.label}
              type="button"
              onClick={() => setActive(i)}
              className={`px-3 py-2 text-xs font-medium whitespace-nowrap transition-colors ${
                i === active
                  ? 'text-white border-b-2 border-primary-400'
                  : 'text-gray-400 hover:text-gray-200 border-b-2 border-transparent'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={copy}
          className="p-1.5 rounded-md text-gray-400 hover:text-white hover:bg-gray-700 transition-colors shrink-0"
          title={t('common.copy')}
        >
          {copied ? <CheckIcon className="w-4 h-4" /> : <ClipboardDocumentIcon className="w-4 h-4" />}
        </button>
      </div>
      <pre className="bg-gray-900 text-gray-100 text-xs leading-relaxed p-4 overflow-x-auto">
        <code>{current.code}</code>
      </pre>
    </div>
  );
}
